import { SidePanel } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { type Company } from 'modules/app/hooks/useCompany';
import { type User } from 'modules/app/hooks/useUser';
import type { CostCenter } from 'modules/budgets/models/costCenter';
import type { Team } from 'modules/budgets/models/team';
import { LazyRequestsPanel } from 'modules/requests/creation/components/LazyRequestsPanel/LazyRequestsPanel';
import { type CustomFieldDefinition } from 'modules/requests/models/customFieldDefinition';
import type { MileageScheme } from 'modules/requests/models/mileageScheme';
import type { RequestAPI } from 'modules/requests/types';
import {
  hasApproverSubNav,
  isDraftsTab,
  isToApproveTab,
  SubnavigationItem,
} from 'modules/requests/utils/navigation';
import { UnexpectedErrorContainer } from 'src/core/common/components/UnexpectedError/UnexpectedErrorContainer';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { routes, routeFor } from 'src/core/constants/routes';
import { type PushNotif } from 'src/core/modules/app/notifications';
import { DraftRequestsTable } from 'src/core/modules/requests/list/components/RequestsListBox/DraftRequestsTable';
import { RequestsTable } from 'src/core/modules/requests/list/components/RequestsListBox/RequestsTable';
import { getRequestTabFromUrlParams } from 'src/core/modules/requests/utils/getRequestTabFromUrlParams';

import { RequestFilters } from './RequestFilters/RequestFilters';
import { RequestsToApproveList } from './RequestsToApproveList/RequestsToApproveList';
import { type RequestStats, type Sections } from './requestsSectionsProps';

type Props = {
  customFields?: CustomFieldDefinition[];
  groups?: Team[];
  costCenters: CostCenter[];
  company: Company;
  user: User;
  itemId?: string;
  newRequestType?: string;
  history: { push: (route: string) => void };
  type: SubnavigationItem;
  updateRequest: () => void;
  addRequestLocally: () => void;
  approveRequest: () => void;
  updateRequestLocally: () => void;
  removeRequestLocally: () => void;
  loadCardRequest: () => void;
  uploadFile: () => void;
  pushNotif: PushNotif;
  sectionsStats: Sections<RequestStats>;
  requestsSections: Sections<
    (RequestAPI & { mileage_scheme: MileageScheme })[]
  >;
  isLoading?: boolean;
  hasFilters?: boolean;
  setRequestFilters: (query: object) => void;
  setTextFilter: (query: string) => void;
};

const Requests = ({
  addRequestLocally,
  approveRequest,
  company,
  costCenters,
  customFields,
  groups,
  hasFilters,
  history,
  isLoading,
  itemId,
  loadCardRequest,
  newRequestType,
  pushNotif,
  removeRequestLocally,
  requestsSections,
  sectionsStats,
  setRequestFilters,
  setTextFilter,
  type,
  user,
  uploadFile,
  updateRequest,
  updateRequestLocally,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
Props) => {
  const isPanelOpen = !!itemId;
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasResults = () => {
    return getAllRequests().length > 0;
  };

  // Only select sections we want to display
  const getActiveRequestsSections = () => {
    if (type === SubnavigationItem.All && hasApproverSubNav(user)) {
      const sections = {
        purchaseRequests: requestsSections.purchaseRequests,
        expenseClaimRequests: requestsSections.expenseClaimRequests,
        invoiceRequests: requestsSections.invoiceRequests,
        purchaseOrderRequests: requestsSections.purchaseOrderRequests,
        mileageAllowanceRequests: requestsSections.mileageAllowanceRequests,
        perDiemAllowanceRequests: requestsSections.perDiemAllowanceRequests,
        creditNoteRequests: requestsSections.creditNoteRequests,
        travelBudgetRequests: requestsSections.travelBudgetRequests,
      };

      return Object.fromEntries(
        Object.entries(sections).filter(([_, value]) => {
          return (value ?? []).length > 0;
        }),
      );
    }

    if (isDraftsTab(type)) {
      const sections = {
        myDraftInvoiceRequests: requestsSections.myDraftInvoiceRequests,
        myDraftCreditNoteRequests: requestsSections.myDraftCreditNoteRequests,
        otherDraftInvoiceRequests: requestsSections.otherDraftInvoiceRequests,
        myDraftExpenseClaimRequests:
          requestsSections.myDraftExpenseClaimRequests,
      };

      return Object.fromEntries(
        Object.entries(sections).filter(([_, value]) => {
          return (value ?? []).length > 0;
        }),
      );
    }

    const sections = {
      myPurchaseRequests: requestsSections.myPurchaseRequests,
      myExpenseClaimRequests: requestsSections.myExpenseClaimRequests,
      myPurchaseOrderRequests: requestsSections.myPurchaseOrderRequests,
      myMileageAllowanceRequests: requestsSections.myMileageAllowanceRequests,
      myPerDiemAllowanceRequests: requestsSections.myPerDiemAllowanceRequests,
      myInvoiceRequests: requestsSections.myInvoiceRequests,
      myCreditNoteRequests: requestsSections.myCreditNoteRequests,
      myTravelBudgetRequests: requestsSections.myTravelBudgetRequests,
      teamPurchaseRequests: requestsSections.teamPurchaseRequests,
      teamExpenseClaimRequests: requestsSections.teamExpenseClaimRequests,
      teamPurchaseOrderRequests: requestsSections.teamPurchaseOrderRequests,
      teamInvoiceRequests: requestsSections.teamInvoiceRequests,
      teamCreditNoteRequests: requestsSections.teamCreditNoteRequests,
      teamMileageAllowanceRequests:
        requestsSections.teamMileageAllowanceRequests,
      teamPerDiemAllowanceRequests:
        requestsSections.teamPerDiemAllowanceRequests,
      teamTravelBudgetRequests: requestsSections.teamTravelBudgetRequests,
    };

    return Object.fromEntries(
      Object.entries(sections).filter(([_, value]) => {
        return (value ?? []).length > 0;
      }),
    );
  };

  const getAllRequests = () => {
    const activeRequestsSections = getActiveRequestsSections();

    const activeRequests = [];
    for (const property in activeRequestsSections) {
      activeRequests.push(...activeRequestsSections[property]);
    }
    return activeRequests;
  };

  const closePanel = () => {
    history.push(routeFor(routes.REQUESTS.path, { company: company.id, type }));
  };

  const renderFilters = () => {
    if (isDraftsTab(type)) {
      return null;
    }

    return (
      <ErrorBoundary
        context={{ scope: 'requests::filter', team: 'travel-and-expenses' }}
        fallbackComponent={<></>}
      >
        <RequestFilters
          costCenters={costCenters ?? []}
          teams={groups ?? []}
          setRequestFilters={setRequestFilters}
          setTextFilter={setTextFilter}
          closeRequestsPanel={closePanel}
          setFiltersOpen={setFiltersOpen}
          filtersOpen={filtersOpen}
        />
      </ErrorBoundary>
    );
  };

  const renderPanel = () => (
    <ErrorBoundary
      context={{ scope: 'requests::panel', team: 'travel-and-expenses' }}
      fallbackComponent={
        <SidePanel onClose={closePanel}>
          <UnexpectedErrorContainer />
        </SidePanel>
      }
    >
      <LazyRequestsPanel
        type={type}
        itemId={itemId}
        newRequestType={newRequestType}
        customFields={customFields}
        groups={groups}
        costCenters={costCenters}
        user={user}
        closePanel={closePanel}
        updateRequest={updateRequest}
        addRequestLocally={addRequestLocally}
        approveRequest={approveRequest}
        updateRequestLocally={updateRequestLocally}
        removeRequestLocally={removeRequestLocally}
        loadCardRequest={loadCardRequest}
        uploadFile={uploadFile}
        pushNotif={pushNotif}
      />
    </ErrorBoundary>
  );

  const shouldHideHeader = !isLoading && !hasResults() && !hasFilters;

  if (isToApproveTab(type)) {
    return (
      <div className="flex-1">
        <div className="flex h-full flex-col px-40">
          {isPanelOpen && renderPanel()}
          <ErrorBoundary
            context={{
              scope: 'requests::to-approve::list',
              team: 'travel-and-expenses',
            }}
          >
            <RequestsToApproveList />
          </ErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <div className="flex h-full flex-col px-40">
        {isPanelOpen && renderPanel()}
        {!shouldHideHeader && renderFilters()}
        <ErrorBoundary
          context={{ scope: 'requests::list', team: 'travel-and-expenses' }}
        >
          {isDraftsTab(type) ? (
            <DraftRequestsTable type={type} />
          ) : (
            <RequestsTable
              sectionsStats={sectionsStats}
              requests={getAllRequests()}
              type={getRequestTabFromUrlParams(type)}
              isLoading={isLoading}
              onOptionClick={(request) => {
                // Open the request panel
                history.push(
                  routeFor(routes.REQUESTS.path, {
                    company: company.id,
                    id: request.id,
                    type,
                  }),
                );
              }}
              hasFiltersApplied={hasFilters}
            />
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Requests;
